import { CheckCircle } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import { PATH } from 'src/constants/path';

export interface IReviewItem {
    id: string;
    itemType: 'QUOTE_REVIEW' | 'BOL_REVIEW' | 'DELIVERY_TIMES' | 'POD_REVIEW' | 'CUSTOMS_DOCUMENT';
    entityType: 'order' | 'quote';
    entityId: number;
    referenceId: string;
    urgencyScore: number;
    createdAt: string;
    // Item-type-specific fields — see getReviewItems.controller.ts response shape.
    shipperCity?: string;
    shipperStateCode?: string;
    consigneeCity?: string;
    consigneeStateCode?: string;
    pickupDate?: string;
    expirationDate?: string;
    consigneeCompanyName?: string;
    deliveryAppointmentStartDateTime?: string;
    deliveryAppointmentEndDateTime?: string;
    deliveryAppointmentHasWindow?: boolean;
    deliveredAt?: string;
    expectedPickupTime?: string;
}

interface IReviewItemDisplay {
    dotColor: string;
    requiresAcknowledgement: boolean;
    getTitle: (item: IReviewItem) => string;
    getSubtitle: (item: IReviewItem) => string;
}

type Translate = (key: string, opts?: Record<string, any>) => string;

const formatLane = (item: IReviewItem, cityKey: 'shipperCity' | 'consigneeCity', stateKey: 'shipperStateCode' | 'consigneeStateCode') => {
    const city = item[cityKey];
    const state = item[stateKey];

    if (!city && !state) return '';

    return [city, state].filter(Boolean).join(', ');
};

const formatDateTime = (value?: string) => (value ? moment.utc(value).local().format('MMM D, h:mm A') : '');

export const getReviewItemDisplayMap = (t: Translate): Record<IReviewItem['itemType'], IReviewItemDisplay> => ({
    QUOTE_REVIEW: {
        dotColor: 'bg-warning500',
        requiresAcknowledgement: false,
        getTitle: (item) => t('Dashboard.ActivityFeed.Quote_Review_Title', { ref: item.referenceId }),
        getSubtitle: (item) => {
            const origin = formatLane(item, 'shipperCity', 'shipperStateCode');
            const destination = formatLane(item, 'consigneeCity', 'consigneeStateCode');
            const lane = origin && destination ? `${origin} → ${destination}` : '';

            return item.expirationDate
                ? t('Dashboard.ActivityFeed.Quote_Review_Desc', { lane, expiry: formatDateTime(item.expirationDate) })
                : t('Dashboard.ActivityFeed.Quote_Review_Desc_No_Expiry', { lane });
        },
    },
    BOL_REVIEW: {
        dotColor: 'bg-fgInfoPrimary',
        requiresAcknowledgement: true,
        getTitle: (item) => t('Dashboard.ActivityFeed.Bol_Review_Title', { ref: item.referenceId }),
        getSubtitle: () => t('Dashboard.ActivityFeed.Bol_Review_Desc'),
    },
    DELIVERY_TIMES: {
        dotColor: 'bg-primary',
        requiresAcknowledgement: true,
        getTitle: (item) => t('Dashboard.ActivityFeed.Delivery_Times_Title', { ref: item.referenceId }),
        getSubtitle: (item) => {
            const window =
                item.deliveryAppointmentHasWindow && item.deliveryAppointmentStartDateTime && item.deliveryAppointmentEndDateTime
                    ? `${formatDateTime(item.deliveryAppointmentStartDateTime)} - ${moment.utc(item.deliveryAppointmentEndDateTime).local().format('h:mm A')}`
                    : formatDateTime(item.deliveryAppointmentStartDateTime);

            return t('Dashboard.ActivityFeed.Delivery_Times_Desc', {
                consignee: item.consigneeCompanyName || t('Dashboard.ActivityFeed.The_Consignee'),
                window,
            });
        },
    },
    POD_REVIEW: {
        dotColor: 'bg-successSecondary',
        requiresAcknowledgement: true,
        getTitle: (item) => t('Dashboard.ActivityFeed.Pod_Review_Title', { ref: item.referenceId }),
        getSubtitle: (item) => t('Dashboard.ActivityFeed.Pod_Review_Desc', { deliveredAt: formatDateTime(item.deliveredAt) }),
    },
    CUSTOMS_DOCUMENT: {
        dotColor: 'bg-fgErrorPrimary',
        requiresAcknowledgement: false,
        getTitle: (item) => t('Dashboard.ActivityFeed.Customs_Document_Title', { ref: item.referenceId }),
        getSubtitle: () => t('Dashboard.ActivityFeed.Customs_Document_Desc'),
    },
});

export { CheckCircle as ReviewItemsEmptyStateIcon };

export const getReviewItemDeepLink = (item: IReviewItem): string => (item.entityType === 'quote' ? `${PATH.MY_QUOTE}/${item.entityId}` : `${PATH.ORDER_VIEW}/${item.entityId}`);

/**
 * Router state handed to the destination page so an action item lands the customer ON the thing
 * it is asking about, rather than on the page with the real task still to be hunted down:
 *   BOL_REVIEW       → the BOL opens in the document preview modal (consumed in OrderView)
 *   CUSTOMS_DOCUMENT → the Upload Customs Documents modal opens (consumed in OrderStatus)
 * Each consumer acts once and then clears the state, so a refresh or a back-navigation to the
 * page does not pop the modal open again. Item types with no modal of their own return undefined
 * and keep the plain redirect.
 */
export const getReviewItemNavState = (item: IReviewItem): Record<string, boolean> | undefined => {
    if (item.itemType === 'BOL_REVIEW') return { openBolPreview: true };
    if (item.itemType === 'CUSTOMS_DOCUMENT') return { openCustomsUpload: true };

    return undefined;
};
